import axios from 'axios';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { api } from 'services/api';
import { PayloadAction } from '@reduxjs/toolkit';
import { PAGE_SIZE } from 'services/constants/env';
import { notificationContainer } from 'utils/notificationContainer';
import {
	getAnnualExchangeRateListFailure,
	getAnnualExchangeRateListRequest,
	getAnnualExchangeRateListSuccess,
	getDailyExchangeRateListFailure,
	getDailyExchangeRateListRequest,
	getDailyExchangeRateListSuccess,
	getEuMonthlyExchangeRateListFailure,
	getEuMonthlyExchangeRateListRequest,
	getEuMonthlyExchangeRateListSuccess,
	getGbpMonthlyExchangeRateListFailure,
	getGbpMonthlyExchangeRateListRequest,
	getGbpMonthlyExchangeRateListSuccess,
	getNonEuExchangeRateListFailure,
	getNonEuExchangeRateListRequest,
	getNonEuExchangeRateListSuccess,
	refreshDailyExchangeRatesFailure,
	refreshDailyExchangeRatesRequest,
	refreshDailyExchangeRatesSuccess,
} from './reducer';
import { getDailyExchangeRateLastListParams } from './selectors';
import {
	TAnnualExchangeRateListRequest,
	TAnnualExchangeRateListResponse,
	TDailyExchangeRateListRequest,
	TDailyExchangeRateListResponse,
	TEuMonthlyExchangeRateListRequest,
	TEuMonthlyExchangeRateListResponse,
	TGbpMonthlyExchangeRateListRequest,
	TGbpMonthlyExchangeRateListResponse,
	TNonEuExchangeRateListRequest,
	TNonEuExchangeRateListResponse,
} from './types';

const DEFAULT_LIST_PARAMS: TDailyExchangeRateListRequest = {
	pageSize: PAGE_SIZE,
	pageNumber: 1,
};

/** Shows the message the API returned, falling back to the passed default. */
function notifyError(error: unknown, fallback: string) {
	if (axios.isAxiosError(error)) {
		notificationContainer(
			String(error?.response?.data?.message ?? error.message ?? fallback),
			'error',
		);
		return;
	}
	notificationContainer(fallback, 'error');
}

function* getDailyExchangeRateListWorker({
	payload,
}: PayloadAction<TDailyExchangeRateListRequest>) {
	try {
		const response: TDailyExchangeRateListResponse = yield call(
			api.exchangeRates.getDailyExchangeRateList,
			payload,
		);
		yield put(getDailyExchangeRateListSuccess(response));
	} catch (error) {
		yield put(getDailyExchangeRateListFailure());
		notifyError(error, 'Failed to load daily exchange rates');
	}
}

function* refreshDailyExchangeRatesWorker() {
	try {
		yield call(api.exchangeRates.refreshDailyExchangeRates);
		const lastParams: TDailyExchangeRateListRequest | null = yield select(
			getDailyExchangeRateLastListParams,
		);
		const response: TDailyExchangeRateListResponse = yield call(
			api.exchangeRates.getDailyExchangeRateList,
			lastParams ?? DEFAULT_LIST_PARAMS,
		);
		yield put(getDailyExchangeRateListSuccess(response));
		yield put(refreshDailyExchangeRatesSuccess());
		notificationContainer('Daily exchange rates were successfully updated', 'success');
	} catch (error) {
		yield put(refreshDailyExchangeRatesFailure());
		notifyError(error, 'Failed to update daily exchange rates');
	}
}

function* getAnnualExchangeRateListWorker({
	payload,
}: PayloadAction<TAnnualExchangeRateListRequest>) {
	try {
		const response: TAnnualExchangeRateListResponse = yield call(
			api.exchangeRates.getAnnualExchangeRateList,
			payload,
		);
		yield put(getAnnualExchangeRateListSuccess(response));
	} catch (error) {
		yield put(getAnnualExchangeRateListFailure());
		notifyError(error, 'Failed to load annual exchange rates');
	}
}

function* getGbpMonthlyExchangeRateListWorker({
	payload,
}: PayloadAction<TGbpMonthlyExchangeRateListRequest>) {
	try {
		const response: TGbpMonthlyExchangeRateListResponse = yield call(
			api.exchangeRates.getGbpMonthlyExchangeRateList,
			payload,
		);
		yield put(getGbpMonthlyExchangeRateListSuccess(response));
	} catch (error) {
		yield put(getGbpMonthlyExchangeRateListFailure());
		notifyError(error, 'Failed to load GBP monthly exchange rates');
	}
}

function* getEuMonthlyExchangeRateListWorker({
	payload,
}: PayloadAction<TEuMonthlyExchangeRateListRequest>) {
	try {
		const response: TEuMonthlyExchangeRateListResponse = yield call(
			api.exchangeRates.getEuMonthlyExchangeRateList,
			payload,
		);
		yield put(getEuMonthlyExchangeRateListSuccess(response));
	} catch (error) {
		yield put(getEuMonthlyExchangeRateListFailure());
		notifyError(error, 'Failed to load EU monthly exchange rates');
	}
}

function* getNonEuExchangeRateListWorker({
	payload,
}: PayloadAction<TNonEuExchangeRateListRequest>) {
	try {
		const response: TNonEuExchangeRateListResponse = yield call(
			api.exchangeRates.getNonEuExchangeRateList,
			payload,
		);
		yield put(getNonEuExchangeRateListSuccess(response));
	} catch (error) {
		yield put(getNonEuExchangeRateListFailure());
		notifyError(error, 'Failed to load UK exchange rates');
	}
}

export function* exchangeRatesSaga() {
	yield takeLatest(getDailyExchangeRateListRequest.type, getDailyExchangeRateListWorker);
	yield takeLatest(refreshDailyExchangeRatesRequest.type, refreshDailyExchangeRatesWorker);
	yield takeLatest(getAnnualExchangeRateListRequest.type, getAnnualExchangeRateListWorker);
	yield takeLatest(getGbpMonthlyExchangeRateListRequest.type, getGbpMonthlyExchangeRateListWorker);
	yield takeLatest(getEuMonthlyExchangeRateListRequest.type, getEuMonthlyExchangeRateListWorker);
	yield takeLatest(getNonEuExchangeRateListRequest.type, getNonEuExchangeRateListWorker);
}
